/**
 * Мой профиль.
 *
 * Здесь человек меняет то, что относится к нему самому: имя и пароль.
 * Адрес электронной почты не меняется — он служит идентификатором при входе,
 * и его смена «на лету» означала бы, что вход по прежнему адресу перестал
 * работать без предупреждения.
 *
 * Смена пароля требует текущего пароля даже при действующей сессии: чужой
 * незаблокированный ноутбук не должен превращаться в захват учётной записи.
 */

import {
  KeyRoundIcon,
  Loader2Icon,
  LogOutIcon,
  SaveIcon,
  SlidersHorizontalIcon,
  UserIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { useChangePassword, useProfile, useUpdateProfile } from '@/api/profile';
import { useClearAvatar, useUploadAvatar } from '@/api/profile';
import { ErrorState } from '@/components/common/states';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDisplaySettings } from '@/app/display-settings';
import { Skeleton } from '@/components/ui/skeleton';
import { ImageUpload } from '@/components/common/image-upload';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDate, formatRelative, pluralRu } from '@/lib/format';

export function ProfilePage() {
  const { data, isPending, isError, error, refetch } = useProfile();

  return (
    <>
      <PageHeader
        title="Мой профиль"
        description="Что о вас знает команда, и как устроен ваш вход"
      />

      {/* Ширина не «растянута», а занята.
          Профиль занимал треть экрана не потому, что был узко свёрстан, а
          потому, что в нём почти нечего было заполнять: имя и пароль. Теперь
          в левой колонке — то, что человек рассказывает о себе, в правой —
          настройки показа, пароль и его собственная активность. */}
      <PageBody className="max-w-6xl space-y-4">
        {isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full" />
            <div className="grid gap-4 lg:grid-cols-2">
              <Skeleton className="h-72 w-full" />
              <Skeleton className="h-72 w-full" />
            </div>
          </div>
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            <ProfileHeader profile={data} />

            <div className="grid items-start gap-4 lg:grid-cols-2">
              <div className="space-y-4">
                <AboutCard profile={data} />
              </div>
              <div className="space-y-4">
                <DisplayCard />
                <PasswordCard />
                <ActivityCard profile={data} />
              </div>
            </div>
          </>
        )}
      </PageBody>
    </>
  );
}

type Profile = NonNullable<ReturnType<typeof useProfile>['data']>;

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const change = useChangePassword();

  const mismatch = confirmation.length > 0 && next !== confirmation;
  const ready = current.length > 0 && next.length >= 12 && next === confirmation;

  const submit = async () => {
    try {
      const result = await change.mutateAsync({
        currentPassword: current,
        newPassword: next,
        newPasswordConfirmation: confirmation,
      });
      setCurrent('');
      setNext('');
      setConfirmation('');
      toast.success(
        result.closedSessions > 0
          ? `Пароль изменён. Закрыто ${pluralRu(result.closedSessions, ['сеанс', 'сеанса', 'сеансов'])} на других устройствах.`
          : 'Пароль изменён.',
      );
    } catch (error) {
      const message =
        error instanceof ApiError
          ? (error.fields?.currentPassword ?? error.fields?.newPassword ?? error.message)
          : 'Не удалось изменить пароль';
      toast.error(message);
    }
  };

  return (
    <Card className="fade-in">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <KeyRoundIcon className="size-4 text-muted-foreground" />
          Пароль
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="password-current">Текущий</Label>
            <Input
              id="password-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password-next">Новый</Label>
            <Input
              id="password-next"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password-confirm">Ещё раз</Label>
            <Input
              id="password-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              aria-invalid={mismatch}
            />
            {mismatch ? <p className="text-2xs text-tone-danger">Пароли не совпадают</p> : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" disabled={!ready || change.isPending} onClick={() => void submit()}>
            {change.isPending ? <Loader2Icon className="animate-spin" /> : <KeyRoundIcon />}
            Изменить пароль
          </Button>
          <p className="max-w-md text-2xs leading-relaxed text-muted-foreground">
            Не короче 12 символов. Смена закроет сеансы на всех других устройствах — текущий
            останется. Проверяется только длина: правила состава символов на практике ухудшают
            пароли.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ActivityCard({ profile }: { profile: Profile }) {
  return (
    <Card className="fade-in">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <LogOutIcon className="size-4 text-muted-foreground" />
          Сеансы и активность
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="flex flex-wrap gap-x-8 gap-y-2">
          <div>
            <dt className="text-2xs text-muted-foreground">Действующих сеансов</dt>
            <dd className="text-sm font-medium tabular-nums">{profile.activeSessions}</dd>
          </div>
          <div>
            <dt className="text-2xs text-muted-foreground">Последнее действие</dt>
            <dd className="text-sm font-medium">
              {profile.lastActivityAt ? formatRelative(profile.lastActivityAt) : 'нет'}
            </dd>
          </div>
        </dl>

        {profile.activeSessions > 1 ? (
          <p className="text-2xs leading-relaxed text-muted-foreground">
            Сеансов больше одного — возможно, вход остался на другом устройстве. Смена пароля
            закрывает все, кроме текущего.
          </p>
        ) : null}

        {profile.recentActivity.length === 0 ? (
          <p className="text-xs text-muted-foreground">Действий пока нет.</p>
        ) : (
          <div>
            <p className="mb-1.5 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
              Последние действия
            </p>
            <ul className="space-y-1">
              {profile.recentActivity.map((entry) => (
                <li key={entry.id} className="flex items-baseline gap-2 text-xs">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="text-muted-foreground">{entry.labelRu}</span>
                    {entry.entityTitle ? <span className="ml-1">{entry.entityTitle}</span> : null}
                  </span>
                  <span className="shrink-0 text-2xs text-muted-foreground/80">
                    {typeof entry.metadata.projectName === 'string'
                      ? entry.metadata.projectName
                      : ''}
                  </span>
                  <span className="shrink-0 text-2xs text-muted-foreground">
                    {formatRelative(entry.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Отображение ---------------------------------------------------------

/**
 * Настройки показа.
 *
 * Это предпочтения одного человека, а не проектные данные: плотность,
 * формат дат и первый день недели у каждого свои и не должны навязываться
 * команде. Хранятся в браузере — общими их делать нечего, а таблица в базе
 * ради оформления добавила бы миграций без пользы.
 */
function DisplayCard() {
  const settings = useDisplaySettings();

  return (
    <Card className="fade-in">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <SlidersHorizontalIcon className="size-4 text-muted-foreground" />
          Отображение
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <SettingRow
            id="setting-density"
            label="Плотность"
            hint="Компактная помещает вдвое больше строк"
            value={settings.density}
            onChange={(value) => settings.set('density', value as typeof settings.density)}
            options={[
              { value: 'comfortable', label: 'Комфортная' },
              { value: 'compact', label: 'Компактная' },
            ]}
          />

          <SettingRow
            id="setting-date"
            label="Формат дат"
            hint="ISO сортируется как текст"
            value={settings.dateFormat}
            onChange={(value) => settings.set('dateFormat', value as typeof settings.dateFormat)}
            options={[
              { value: 'dd.mm.yyyy', label: `ДД.ММ.ГГГГ — ${sample('dd.mm.yyyy')}` },
              { value: 'iso', label: `ГГГГ-ММ-ДД — ${sample('iso')}` },
            ]}
          />

          <SettingRow
            id="setting-week"
            label="Первый день недели"
            hint="Влияет на календарь"
            value={settings.weekStart}
            onChange={(value) => settings.set('weekStart', value as typeof settings.weekStart)}
            options={[
              { value: 'monday', label: 'Понедельник' },
              { value: 'sunday', label: 'Воскресенье' },
            ]}
          />
        </div>

        <p className="text-2xs leading-relaxed text-muted-foreground/80">
          Настройки хранятся в этом браузере: на другом устройстве их нужно выбрать заново. Язык
          интерфейса пока только русский.
        </p>
      </CardContent>
    </Card>
  );
}

/** Пример даты в выбранном формате: подпись понятнее описания. */
function sample(style: 'dd.mm.yyyy' | 'iso'): string {
  const date = new Date(2026, 7, 24);
  return style === 'iso'
    ? '2026-08-24'
    : new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
}

function SettingRow({
  id,
  label,
  hint,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-2xs text-muted-foreground">{hint}</p>
    </div>
  );
}

// --- Фотография ----------------------------------------------------------

// --- Шапка профиля ---------------------------------------------------------

/**
 * Крупный аватар, имя и чем человек занимается.
 *
 * Фотография меняется здесь же, а не отдельной карточкой ниже: она и есть
 * первое, на что смотрят, и прятать её в третий блок было бы странно.
 */
function ProfileHeader({ profile }: { profile: Profile }) {
  const upload = useUploadAvatar();
  const clear = useClearAvatar();

  return (
    <Card className="fade-in">
      <CardContent className="flex flex-wrap items-center gap-5 py-1">
        <ImageUpload
          round
          label="Загрузить фото"
          hint="Квадратное изображение выглядит лучше: аватар круглый. До 5 МБ."
          canClear={Boolean(profile.user.avatarUrl)}
          preview={<UserAvatar user={profile.user} size="xl" />}
          onUpload={(file) => upload.mutateAsync(file)}
          onClear={() => clear.mutateAsync()}
        />

        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-semibold">{profile.user.name}</p>
          <p className="truncate text-sm text-muted-foreground">
            {profile.title || 'Должность не указана'}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-2xs text-muted-foreground">
            <span>{profile.user.email}</span>
            {profile.location ? <span>· {profile.location}</span> : null}
            <span>· в системе с {formatDate(profile.createdAt)}</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// --- О себе ----------------------------------------------------------------

/**
 * Всё, что человек рассказывает команде о себе.
 *
 * Поля выбраны не для заполнения места, а по вопросам, которые в небольшой
 * команде задают друг о друге на самом деле: чем занимается, где находится,
 * как достучаться и что о себе говорит.
 *
 * Особенно важен способ связи: система намеренно не имеет внешних каналов и
 * никому ничего не шлёт. Значит, «как меня найти» должно быть записано там,
 * где это увидят, — иначе его спрашивают в переписке каждый раз заново.
 *
 * Одна кнопка на всю карточку, а не сохранение по каждому полю: человек
 * заполняет профиль подряд, и пять подтверждений вместо одного превратили бы
 * это в допрос.
 */
function AboutCard({ profile }: { profile: Profile }) {
  const [form, setForm] = useState({
    name: profile.user.name,
    title: profile.title ?? '',
    location: profile.location ?? '',
    contact: profile.contact ?? '',
    bio: profile.bio ?? '',
  });
  const update = useUpdateProfile();

  const trimmed = {
    name: form.name.trim(),
    title: form.title.trim(),
    location: form.location.trim(),
    contact: form.contact.trim(),
    bio: form.bio.trim(),
  };

  const changed =
    trimmed.name !== profile.user.name ||
    trimmed.title !== (profile.title ?? '') ||
    trimmed.location !== (profile.location ?? '') ||
    trimmed.contact !== (profile.contact ?? '') ||
    trimmed.bio !== (profile.bio ?? '');

  const valid = trimmed.name.length >= 2;

  const save = async () => {
    try {
      await update.mutateAsync(trimmed);
      toast.success('Профиль сохранён');
    } catch (apiError) {
      toast.error(apiError instanceof ApiError ? apiError.message : 'Не удалось сохранить');
    }
  };

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm((previous) => ({ ...previous, [key]: event.target.value })),
  });

  return (
    <Card className="fade-in">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <UserIcon className="size-4 text-muted-foreground" />О себе
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="profile-name">Имя</Label>
            <Input id="profile-name" maxLength={200} {...field('name')} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-title">Чем занимаетесь</Label>
            <Input
              id="profile-title"
              maxLength={160}
              placeholder="Например: продукт и юридические вопросы"
              {...field('title')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-location">Где вы</Label>
            <Input
              id="profile-location"
              maxLength={120}
              placeholder="Берлин"
              {...field('location')}
            />
            <p className="text-2xs text-muted-foreground">
              Чтобы коллеги понимали разницу во времени.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="profile-contact">Как связаться</Label>
            <Input
              id="profile-contact"
              maxLength={200}
              placeholder="@username в Telegram"
              {...field('contact')}
            />
            <p className="text-2xs leading-relaxed text-muted-foreground">
              Система не шлёт ни писем, ни сообщений — договариваются вне её.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="profile-bio">Коротко о себе</Label>
          <Textarea
            id="profile-bio"
            rows={3}
            maxLength={400}
            placeholder="За что отвечаете, что у вас лучше спрашивать, чем можете помочь."
            {...field('bio')}
          />
          <p className="text-2xs text-muted-foreground tabular-nums">
            {form.bio.trim().length} из 400
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="profile-email">Адрес</Label>
          <Input id="profile-email" value={profile.user.email} disabled readOnly />
          <p className="text-2xs leading-relaxed text-muted-foreground">
            Адрес — идентификатор при входе. Его меняет администратор: смена «на лету» означала бы,
            что вход по прежнему адресу перестал работать без предупреждения.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            disabled={!changed || !valid || update.isPending}
            onClick={() => void save()}
          >
            {update.isPending ? <Loader2Icon className="animate-spin" /> : <SaveIcon />}
            Сохранить
          </Button>
          <p className="text-2xs text-muted-foreground">
            {changed ? 'Есть несохранённые изменения' : 'Всё сохранено'} · видно участникам ваших
            проектов
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
